import { useEffect } from "react";
import ApiError from "../lib/ApiError";
import { toast } from "react-toastify";
import { ReactQueryGlobalErrorHandler } from "../lib";
import { getGetSupplierInfo } from "../constants/reactQueryKey";
import { trackException } from "../lib/appInsightsFactory";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
function useErrorHandler() {
    useEffect(() => {
        // Handle synchronous errors
        const handleError = (event: ErrorEvent) => {
            if (!(event.error instanceof ApiError)) {
                if (process.env.NODE_ENV === "development") {
                    console.log("Error caught:", event.error);
                } else {
                    toast.error("Ein unerwarteter Fehler ist aufgetreten");
                }
                trackException(event.error, SeverityLevel.Error);
            }
            event.preventDefault();
        };

        // Handle unhandled promise rejections (async errors)
        const handlePromiseRejection = (event: PromiseRejectionEvent) => {
            if (process.env.NODE_ENV === "development") {
                console.log(
                    "Unhandled Promise Rejection caught:",
                    event.reason
                );
            } else {
                toast.error("Ein unerwarteter Fehler ist aufgetreten");
            }
            trackException(event.reason, SeverityLevel.Error);
            event.preventDefault();
        };

        // Add event listeners for synchronous and async errors
        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handlePromiseRejection);

        // Clean up event listeners on component unmount
        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener(
                "unhandledrejection",
                handlePromiseRejection
            );
        };
    }, []);

    const customQueryErrorHandler: ReactQueryGlobalErrorHandler = (
        error,
        query
    ) => {
        if (error instanceof ApiError) {
            // Show message we got from the backend.
            if (
                error.status === 400 &&
                error.data &&
                error.data.code === 1001 &&
                error.data.frontendMessage
            ) {
                toast.error(error.data.frontendMessage);
                // For [getGetSupplierInfo] show 500 error page.
            } else if (
                query && // Only for queries
                "queryKey" in query && // Ensure it's a Query object
                typeof query.queryKey[0] === "string" &&
                ![getGetSupplierInfo].includes(query.queryKey[0])
            ) {
                if (error.status === 500 || error.status === 503) {
                    window.location.href = "/500"; // Redirect for critical errors
                }
                // If we get no frontend message from the backend or a 500 response we show a generic toast message.
            } else {
                toast.error(
                    "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut!"
                );
            }
        }
    };

    const customMutationErrorHandler: ReactQueryGlobalErrorHandler = (
        error
    ) => {
        if (error instanceof ApiError) {
            // Show message we got from the backend.
            if (
                error.status === 400 &&
                error.data &&
                error.data.code === 1001 &&
                error.data.frontendMessage
            ) {
                toast.error(error.data.frontendMessage);
                // If we get no frontend message from the backend or a 500 response we show a generic toast message.
            } else {
                toast.error(
                    "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut!"
                );
            }
        }
    };

    return { customQueryErrorHandler, customMutationErrorHandler };
}

export default useErrorHandler;
